import React, { useState, useEffect, useRef } from 'react';

/**
 * StateInspector — collapsible live view of the SDLCState snapshot.
 *
 * Props:
 *   sdlcState  {object|null}  — latest state snapshot from state_update events
 *
 * Highlights fields that changed in the last update for 2 seconds.
 */

// Render a single value with appropriate color class
function ValueSpan({ val }) {
  if (val === null || val === undefined) {
    return <span className="state-val null-val">null</span>;
  }
  if (typeof val === 'boolean') {
    return <span className={`state-val ${val ? 'bool-true' : 'bool-false'}`}>{String(val)}</span>;
  }
  if (typeof val === 'number') {
    return <span className="state-val num-val">{val}</span>;
  }
  if (typeof val === 'string') {
    // Truncate long strings
    const display = val.length > 80 ? val.slice(0, 80) + '…' : val;
    return <span className="state-val">"{display}"</span>;
  }
  return <span className="state-val">{JSON.stringify(val)}</span>;
}

// Collapsible row for arrays/objects
function ExpandableRow({ label, val, updated }) {
  const [open, setOpen] = useState(false);
  const isArray  = Array.isArray(val);
  const count    = isArray ? val.length : Object.keys(val).length;
  const summary  = isArray ? `[${count} items]` : `{${count} keys}`;

  return (
    <div>
      <div
        className={`state-field state-expandable ${updated ? 'updated' : ''}`}
        onClick={() => setOpen(o => !o)}
      >
        <span className="state-key">├─ {label}</span>
        <span className="state-colon">:</span>
        <span className="state-val">{summary} {open ? '▲' : '▼'}</span>
      </div>
      {open && (
        <div className="state-children">
          {isArray
            ? val.map((item, i) => (
                <div key={i} className="state-field">
                  <span className="state-key">[{i}]</span>
                  <span className="state-colon">:</span>
                  <span className="state-val">
                    {typeof item === 'object' ? JSON.stringify(item) : String(item)}
                  </span>
                </div>
              ))
            : Object.entries(val).map(([k, v]) => (
                <div key={k} className="state-field">
                  <span className="state-key">{k}</span>
                  <span className="state-colon">:</span>
                  <ValueSpan val={v} />
                </div>
              ))
          }
        </div>
      )}
    </div>
  );
}

export default function StateInspector({ sdlcState }) {
  // Track which keys changed in the last update for highlight animation
  const prevStateRef = useRef(null);
  const [updatedKeys, setUpdatedKeys] = useState(new Set());

  useEffect(() => {
    if (!sdlcState) return;
    const prev = prevStateRef.current;
    if (prev) {
      const changed = new Set();
      for (const key of Object.keys(sdlcState)) {
        if (JSON.stringify(sdlcState[key]) !== JSON.stringify(prev[key])) {
          changed.add(key);
        }
      }
      setUpdatedKeys(changed);
      // Clear highlights after 2 seconds
      const t = setTimeout(() => setUpdatedKeys(new Set()), 2000);
      return () => clearTimeout(t);
    }
    prevStateRef.current = sdlcState;
  }, [sdlcState]);

  useEffect(() => {
    if (sdlcState) prevStateRef.current = sdlcState;
  }, [sdlcState]);

  if (!sdlcState) {
    return (
      <div className="card">
        <div className="card-header">📸 Pipeline State</div>
        <div className="state-placeholder">Waiting for pipeline to start…</div>
      </div>
    );
  }

  // Fields to render in order
  const fields = [
    'request', 'pillar', 'app_inventory', 'rationalization_scores',
    'spec_document', 'code_artifacts', 'test_results', 'security_report',
    'human_approval_required', 'human_approved', 'deployment_status',
  ];

  return (
    <div className="card">
      <div className="card-header">📸 Pipeline State</div>
      <div className="state-tree">
        {fields.map(key => {
          const val     = sdlcState[key];
          const updated = updatedKeys.has(key);

          if (val === undefined) return null;

          // Arrays and objects get an expandable row
          if (val !== null && typeof val === 'object') {
            return (
              <ExpandableRow key={key} label={key} val={val} updated={updated} />
            );
          }

          return (
            <div key={key} className={`state-field ${updated ? 'updated' : ''}`}>
              <span className="state-key">├─ {key}</span>
              <span className="state-colon">:</span>
              <ValueSpan val={val} />
            </div>
          );
        })}
      </div>
    </div>
  );
}
